// app/javascript/projects/components/ProjectsDisplay.jsx

import React, { useCallback, useRef } from 'react';
var _ = require('underscore');

  // this function gets the index of the codeSpan, highlightSpan, sentenceSpan, and paragraph
  // which you need to calculate the start and end of the selection
function getSpanIndex(node, type) {
  const span = node.closest(`.${type}Span`);
  if ( !span ) return {};

  const index = parseInt(span.getAttribute(`data-${type}-index`));
  return {span, index};
}

function getCodeSpanIndex(node) {
  return getSpanIndex(node, 'code');
}

function getHighlightSpanIndex(node) {
  return  getSpanIndex(node, 'highlight');
}

function getSentenceSpanIndex(node) {
  return getSpanIndex(node, 'sentence');
}

function getParagraphIndex(node) {
  return getSpanIndex(node, 'paragraph');
}

function _getParagraphStartEnd(node) {
  const {
    index: paragraphIndex,
    span: paragraph
  } = getParagraphIndex(node);

  const start = parseInt(paragraph.getAttribute('start'));
  const end = parseInt(paragraph.getAttribute('end'));
  return {
    paragraphIndex,
    start,
    end
  }
}

function getExcerptId(node) {
  const {span: sentenceSpan, index: sentenceIndex} = getSentenceSpanIndex(node);
  if ( !sentenceSpan ) return null;

  return sentenceSpan.getAttribute('excerpt-id') || null;
}

function getCodeSpanStartEnd(node) {
  const {span: codeSpan} = getCodeSpanIndex(node);
  if ( !codeSpan ) return {};
  return {
    start: parseInt(codeSpan.getAttribute('start')),
    end: parseInt(codeSpan.getAttribute('end'))
  }
}

function getClickedSpan(node) {
  return getCodeSpanStartEnd(node);
}

function _isClick(startNode, endNode, startOffset, endOffset) {
  // not sure quite what would cause this, but if we don't have a start or end hash
  // we should return true because there is no selection
  // I have noticed in cypress startHash and endHash are sometimes null
  // when a user clicks on the x button to remove a highlight
  if ( startOffset != endOffset ) return false;

  // loop through the span types, and check if they are equal
  // if any are not equal, return false
  const getSpanFunctions = [getCodeSpanIndex, getHighlightSpanIndex, getSentenceSpanIndex, getParagraphIndex];
  return getSpanFunctions.every((func) => {
    const {index: startIndex} = func(startNode);
    const {index: endIndex} = func(endNode);
    return startIndex === endIndex;
  });
}

function getNodeFromContainer(node) {
  return node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
}


function getStartNodeAndEndNode() {
  var s = window.getSelection();

  if ( !s || s.rangeCount == 0 )
    return {}

  var startRange = s.getRangeAt(0);
  var endRange = s.getRangeAt(s.rangeCount - 1);

  const startNode = getNodeFromContainer(startRange.startContainer);
  const endNode =  getNodeFromContainer(endRange.endContainer);    

  return {
    startNode,
    endNode,
    startRange,
    endRange
  }
}

function  clickedOnCodeTag(isClick, event) {
  if ( !isClick ) return false;

  const _target = !!event && event.target || {};

  const codeTagContainer = _target.closest('.code-tag-container');
  return ( !! codeTagContainer );
}

function isNodeInCodeList(node) {
  return !!node.closest('.codelist');
}

function isNodeOnParagraphIntro(node) {
  return !!node.closest('.speaker') || !!node.closest('.left-hand-transcript'); 
}

function isOutsideOfCodeable(node) {
  return !node.closest('.paragraphSpan');
}

const getLoc = (container, node, offset, isStart, visibleAtStart) => {
  if (isNodeInCodeList(node)) {
    return _getParagraphStartEnd(node).end;
  } else if (isNodeOnParagraphIntro(node)) {
    return _getParagraphStartEnd(node).start;
  } else if (isOutsideOfCodeable(node)) {
    // The boundary landed outside the transcript text (e.g. dragged into the
    // margin or above/below the text). Snap to the paragraph that was at the
    // top/bottom of the viewport WHEN THE SELECTION STARTED, not now: Chrome
    // auto-scrolls the transcript while drag-selecting to the window edge, so
    // the live first/last-visible paragraph would be the very top/bottom of the
    // whole transcript and over-select. Fall back to live computation only if
    // we don't have a mousedown snapshot.
    const visible = visibleAtStart || {};
    if (isStart) return (visible.first || getFirstVisibleNode(container)).start;
    return (visible.last || getLastVisibleNode(container)).end;
  } else {
    return getCodeSpanStartEnd(node).start + offset;
  }
};

function getVisibleNode(container, findLast = false) {
  if ( !container ) return {};
  
  const nodes = container.querySelectorAll('.paragraphSpan');

  let returnValue = {}; // Default return value if no visible node is found

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[findLast ? nodes.length - 1 - i : i]; // Choose direction based on `findLast`
    const rect = node.getBoundingClientRect();

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const isVisible = rect.bottom > 0 && rect.top < viewportHeight; // Any part of node is within viewport


    if (isVisible) {
      
      returnValue = {
        start: parseInt(node.getAttribute('start')),
        end: parseInt(node.getAttribute('end')),
      };

      break;
    }
  }

  return returnValue;
}

function getFirstVisibleNode(container) {
  return getVisibleNode(container, false)
}

function getLastVisibleNode(container) {
  return getVisibleNode(container, true)
}

const CodeableWrapper = ({
  onDeselect,
  onSelectExcerpt,
  onSelectText,
  isPrint,
  hasEditPermission,
  surveyQuestionId,
  codeableType,
  codeableId,
  style,
  enableClickFallback = false,
  children
}) => {
  const wrapperRef = useRef();
  // Snapshot of the first/last visible paragraph at the moment selection began,
  // used to anchor the out-of-bounds fallback in getLoc before Chrome's
  // drag-select auto-scroll can move the viewport. See getLoc / onMouseDown.
  const visibleAtMouseDownRef = useRef({});

  const onMouseUp = useCallback((event) => {
    document.removeEventListener('mouseup', onMouseUp);

    if (isPrint) return;

    // Ignore clicks inside code selector poppers (rendered via portal outside the wrapper)
    const target = event.target;
    if (target?.closest?.('[role="listbox"]') || target?.closest?.('#code-searcher') || target?.closest?.('.MuiPopper-root')) return;

    if (!hasEditPermission) {
      onDeselect?.();
      return;
    }

    const {
      startNode,
      endNode,
      startRange,
      endRange
    } = getStartNodeAndEndNode();

    if (!startNode || !endNode) {
      // No browser selection range found. This happens with programmatic clicks
      // (e.g., Cypress test automation) or assistive technology interactions.
      // Only use the fallback on pages that opt in (e.g., comparison page).
      if (enableClickFallback) {
        if (clickedOnCodeTag(true, event)) return;
        const excerptId = getExcerptId(event.target);
        if (excerptId && onSelectExcerpt) {
          onSelectExcerpt(excerptId);
        } else {
          const { start, end } = getClickedSpan(event.target);
          if (start !== undefined && end !== undefined && onSelectText) {
            onSelectText(start, end, codeableId, codeableType || surveyQuestionId);
          } else {
            onDeselect?.();
          }
        }
      }
      return;
    }

    const startOffset = startRange.startOffset;
    const endOffset = endRange.endOffset;

    const isClick = _isClick(startNode, endNode, startOffset, endOffset);

    if (isClick) {
      if (clickedOnCodeTag(isClick, event)) return;

      const excerptId = getExcerptId(event.target);

      if (excerptId && onSelectExcerpt) {
        onSelectExcerpt(excerptId);
        return;
      }

      const { start, end } = getClickedSpan(event.target);

      if (start !== undefined && end !== undefined && onSelectText) {
        onSelectText(start, end, codeableId, codeableType || surveyQuestionId);
        return;
      }

      onDeselect?.();
      return;
    } else {
      document.getSelection().removeAllRanges();

      if (onSelectText) {
        const visibleAtStart = visibleAtMouseDownRef.current;
        const start = getLoc(wrapperRef.current, startNode, startOffset, true, visibleAtStart);
        const end = getLoc(wrapperRef.current, endNode, endOffset, false, visibleAtStart);
        onSelectText(start, end, codeableId, codeableType || surveyQuestionId);
      }
    }
  }, [isPrint, hasEditPermission, onDeselect, onSelectExcerpt, onSelectText, enableClickFallback, codeableId, codeableType, surveyQuestionId]);

  const onMouseDown = useCallback(() => {
    // Snapshot the visible paragraphs before any drag-select auto-scroll, so the
    // out-of-bounds fallback in getLoc anchors to what the user actually saw.
    visibleAtMouseDownRef.current = {
      first: getFirstVisibleNode(wrapperRef.current),
      last: getLastVisibleNode(wrapperRef.current),
    };
    document.addEventListener('mouseup', onMouseUp);
  }, [onMouseUp]);

  return (
    <div onMouseDown={onMouseDown} ref={wrapperRef} style={style}>
      {children}
    </div>
  );
};

export default CodeableWrapper;
